/* eslint-disable new-cap */
import { BlueprintInstanceType } from '../shared.enums';
import { Connection } from './connection.class';
import { Publish } from '../../decorators';

import type { ConnectionHalfParams, ConnectionOptions, ConnectionsDataIn, ConnectionsDataOut } from './types';
import { deleteConnectionCfn, moveSourceIslandCfn, newConnectionCfn } from './functions';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export class Connections extends KitchenDesignBase<ConnectionsDataIn> {
	readonly children: Array<Connection>;

	constructor (options: KitchenDesignOptions<ConnectionsDataIn> = {}) {
		super(BlueprintInstanceType.Connections, options);

		this.children = [];
		if (options.dataIn?.children && options.dataIn.children.length > 0) {
			options.dataIn.children.forEach((n) => this.children.push(new Connection(null, { dataIn: n, parent: this })));
		}
		this.setInitialized();

		// console.log('new Connections', this, options);
	}

	/**
	 * Provides a means for linking two islands.  The source island is relocated to connect to the target.
	 * @param source The island being connected
	 * @param target The island connecting to
	 * @param options ConnectionOptions
	 */
	public newConnection (source: ConnectionHalfParams, target: ConnectionHalfParams, options: ConnectionOptions): void {
		newConnectionCfn(this, { source, target, options, islands: this.refMap.islands ?? null, obstructions: this.refMap.obstructions ?? null });
	}

	/**
	 * Called after both source and target connections are created to trigger rules engine.
	 * This ensures passthrough rules run only after both connections exist.
	 */
	public addConnection (connection: Connection): void {
		// Connection already added to children array - this method triggers the publish
		// with refMap that includes islands so passthrough rules can run
		if (this.initialized) {
			const kitchen = this?.refMap.kitchen;
			const refMapWithIslands = {
				...this.refMap,
				islands: kitchen?.design?.islands ?? this.refMap.islands,
				obstructions: kitchen?.design?.obstructions ?? this.refMap.obstructions
			};
			this.publish('connections', 'added', {
				ref: refMapWithIslands,
				old: null,
				new: connection
			});
		}
	}
	@Publish('connections', 'deleted')
	public deleteConnection (panelId: string, targetIslandId: string): boolean {
		return deleteConnectionCfn(this, { panelId, targetIslandId });
	}

	public deleteSourceConnection (): boolean {
		const [sc] = this.getSourceConnections();
		if (!sc) return false;
		return deleteConnectionCfn(this, { panelId: sc.sourcePanelId, targetIslandId: sc.targetIslandId });
	}

	public findConnection (sourcePanelId: string, targetIslandId: string): Connection {
		return this.children.find((c) => c.sourcePanelId === sourcePanelId && c.targetIslandId === targetIslandId);
	}

	public getSourceConnections (): Array<Connection> { return this.children.filter((c) => c.isSource) as Array<Connection> }
	public getTargetConnections (): Array<Connection> { return this.children.filter((c) => !c.isSource) as Array<Connection> }

	public getConnectionsBySourcePanelId (panelId: string): Array<Connection> {
		return this.children.filter((record: Connection) => record.sourcePanelId === panelId);
	}

	public findConnectionsByTargetIslandId (islandId: string): Connection | null {
		return this.children.find((record: Connection) => record.targetIslandId === islandId) || null;
	}

	/**
	 * clear
	 * empties all connections
	 */
	public clear (): void {
		this.children.splice(0, this.children.length);
	}

	public updateSourceIslandPositions (): void {
		this.getTargetConnections().forEach((connection) => {
			moveSourceIslandCfn(connection);
		});
	}

	public validate (): boolean {
		const invalidEntries = this.children.filter((record: Connection) => record.validate() === false);
		return invalidEntries.length === 0;
	}

	public serialize ():ConnectionsDataOut {
		return {
			children: this.children.map((connection: Connection) => connection.serialize())
		};
	}
}
